import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, X, CheckCircle2, Circle, AlertTriangle, Clock, Calendar,
  Factory, ShoppingCart, FileCheck, IndianRupee, Users,
  Wrench, Boxes, Sparkles, Search, Trash2, ListChecks, ArrowDown,
  Bell,
} from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import VoiceInput from '../components/VoiceInput';
import StandupModal from '../components/StandupModal';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MODULE_CONFIG = {
  Production:  { color: 'text-purple-400',  bg: 'bg-purple-900/40',  border: 'border-purple-700',  dot: 'bg-purple-400', icon: Factory },
  Sales:       { color: 'text-blue-400',    bg: 'bg-blue-900/40',    border: 'border-blue-700',    dot: 'bg-blue-400',   icon: ShoppingCart },
  Compliance:  { color: 'text-amber-400',   bg: 'bg-amber-900/40',   border: 'border-amber-700',   dot: 'bg-amber-400',  icon: FileCheck },
  Finance:     { color: 'text-green-400',   bg: 'bg-green-900/40',   border: 'border-green-700',   dot: 'bg-green-400',  icon: IndianRupee },
  Workforce:   { color: 'text-cyan-400',    bg: 'bg-cyan-900/40',    border: 'border-cyan-700',    dot: 'bg-cyan-400',   icon: Users },
  Maintenance: { color: 'text-orange-400',  bg: 'bg-orange-900/40',  border: 'border-orange-700',  dot: 'bg-orange-400', icon: Wrench },
  Inventory:   { color: 'text-teal-400',    bg: 'bg-teal-900/40',    border: 'border-teal-700',    dot: 'bg-teal-400',   icon: Boxes },
  Misc:        { color: 'text-zinc-400',    bg: 'bg-zinc-800',       border: 'border-zinc-700',    dot: 'bg-zinc-400',   icon: Sparkles },
};

const PRIORITY_STYLE = {
  Critical: 'bg-red-900/50 text-red-300 border-red-700',
  High:     'bg-orange-900/40 text-orange-300 border-orange-700',
  Medium:   'bg-amber-900/40 text-amber-300 border-amber-700',
  Low:      'bg-zinc-800 text-zinc-400 border-zinc-700',
};

const STATUS_FLOW = ['Planning', 'In Progress', 'Delayed', 'Completed'];
const STATUS_STYLE = {
  'Planning':    'bg-zinc-800 text-zinc-300 border-zinc-600',
  'In Progress': 'bg-blue-900/50 text-blue-300 border-blue-600',
  'Delayed':     'bg-red-900/40 text-red-300 border-red-700',
  'Completed':   'bg-green-900/40 text-green-300 border-green-700',
  'Pending':     'bg-zinc-800 text-zinc-300 border-zinc-600',
  'Packing':     'bg-purple-900/40 text-purple-300 border-purple-700',
  'Dispatched':  'bg-green-900/40 text-green-300 border-green-700',
  'Received':    'bg-green-900/40 text-green-300 border-green-700',
};

const MODULES = ['All', 'Production', 'Sales', 'Compliance', 'Finance', 'Workforce', 'Maintenance', 'Inventory', 'Misc'];
const PRIORITIES = ['All', 'Critical', 'High', 'Medium', 'Low'];

export default function TaskManager() {
  const { user } = useAuth();
  // Default scope: non-admin/owner/manager users see only their own tasks; privileged
  // roles default to "all" so they can oversee the team.
  const isPrivileged = ['admin', 'owner', 'manager'].includes((user?.role || '').toLowerCase());
  const [tasks, setTasks]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [moduleFilter, setModule]     = useState('All');
  const [priorityFilter, setPriority] = useState('All');
  const [statusFilter, setStatus]     = useState('active');
  const [search, setSearch]           = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [employees, setEmployees]     = useState([]);
  const [form, setForm]               = useState({ title: '', description: '', module: 'Misc', priority: 'Medium', due_date: '', reminder_at: '', amount: '', assigned_to: '' });
  const [submitting, setSubmitting]   = useState(false);
  const [showStandup, setShowStandup] = useState(false);
  const [standupForDate, setStandupForDate] = useState(null);
  const [standupDone, setStandupDone] = useState(false);
  const [scopeFilter, setScope]       = useState(isPrivileged ? 'all' : 'mine'); // 'all' | 'mine'

  const location = useLocation();
  const navigate = useNavigate();

  // Deep-link: /tasks?standup=1[&for=YYYY-MM-DD] opens the Standup seed modal
  // (fired from the 10:00 IST morning reminder + 19:00 IST evening 'plan
  // tomorrow' reminder). The `for` param flips the modal copy to tomorrow.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('standup') === '1' && !standupDone) {
      const forParam = params.get('for');
      if (forParam && /^\d{4}-\d{2}-\d{2}$/.test(forParam)) {
        setStandupForDate(forParam);
      } else {
        setStandupForDate(null);
      }
      setShowStandup(true);
    }
  }, [location.search, standupDone]);

  const fetchData = async () => {
    try {
      const [tRes, eRes, uRes] = await Promise.all([
        axios.get(`${API}/tasks/overview`, { withCredentials: true }),
        axios.get(`${API}/workforce/employees`, { withCredentials: true }),
        axios.get(`${API}/users/assignable`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      // Merge users + workforce employees into a single dropdown list.
      // Users first (they have login access), then workforce-only names.
      const userNames = new Set((uRes.data || []).map(u => u.name));
      const merged = [
        ...(uRes.data || []).map(u => ({ id: `u-${u.id}`, name: u.name, role: u.role, source: 'user' })),
        ...(eRes.data || [])
          .filter(em => !userNames.has(em.name))
          .map(em => ({ id: `e-${em.id}`, name: em.name, role: em.role, source: 'employee' })),
      ];
      setTasks(tRes.data); setEmployees(merged);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchData);

  useEffect(() => { fetchData(); }, []);

  const updateTaskStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/tasks/${id}`, { status }, { withCredentials: true });
      setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t));
      if (status === 'Completed') toast.success('Task completed!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating status'); }
  };

  const cycleStatus = (task) => {
    const isDone = ['Completed', 'Dispatched', 'Received'].includes(task.status);
    if (isDone) return;
    const idx = STATUS_FLOW.indexOf(task.status);
    const next = STATUS_FLOW[(idx + 1) % STATUS_FLOW.length];
    updateTaskStatus(task.id, next);
  };

  const toggleComplete = (task) => {
    const isDone = ['Completed', 'Dispatched', 'Received'].includes(task.status);
    updateTaskStatus(task.id, isDone ? 'Planning' : 'Completed');
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await axios.post(`${API}/tasks/`, { ...form, amount: parseFloat(form.amount) || 0 }, { withCredentials: true });
      toast.success('Task created' + (form.reminder_at ? ' · reminder set' : ''));
      setShowModal(false);
      setForm({ title: '', description: '', module: 'Misc', priority: 'Medium', due_date: '', reminder_at: '', amount: '', assigned_to: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const clearCompleted = async () => {
    const done = tasks.filter(t => ['Completed', 'Dispatched', 'Received'].includes(t.status) && t.source === 'custom');
    if (!done.length) { toast('No completed custom tasks to clear'); return; }
    try {
      await Promise.all(done.map(t => axios.delete(`${API}/tasks/${t.id}`, { withCredentials: true })));
      toast.success(`Cleared ${done.length} completed task(s)`);
      fetchData();
    } catch { toast.error('Error clearing tasks'); }
  };

  const getDaysLeft = (due) => {
    if (!due) return null;
    try { return Math.ceil((new Date(due) - new Date()) / 86400000); } catch { return null; }
  };

  const filtered = tasks.filter(t => {
    if (moduleFilter !== 'All' && t.module !== moduleFilter) return false;
    if (priorityFilter !== 'All' && t.priority !== priorityFilter) return false;
    if (statusFilter === 'active' && ['Completed', 'Dispatched', 'Received'].includes(t.status)) return false;
    if (statusFilter === 'done' && !['Completed', 'Dispatched', 'Received'].includes(t.status)) return false;
    if (scopeFilter === 'mine') {
      const me = (user?.name || '').trim().toLowerCase();
      const myEmail = (user?.email || '').trim().toLowerCase();
      const a = (t.assigned_to || '').trim().toLowerCase();
      const c = (t.created_by || '').trim().toLowerCase();
      const isMine = (me && (a === me || c === me)) || (myEmail && (a === myEmail || c === myEmail));
      if (!isMine) return false;
    }
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !t.module.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Sort: active first, then overdue, then by priority, completed at bottom
  const sorted = [...filtered].sort((a, b) => {
    const aDone = ['Completed', 'Dispatched', 'Received'].includes(a.status) ? 1 : 0;
    const bDone = ['Completed', 'Dispatched', 'Received'].includes(b.status) ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    if (a.is_overdue && !b.is_overdue) return -1;
    if (!a.is_overdue && b.is_overdue) return 1;
    const priOrder = { Critical: 0, High: 1, Medium: 2, Low: 3 };
    return (priOrder[a.priority] || 3) - (priOrder[b.priority] || 3);
  });

  const overdue   = tasks.filter(t => t.is_overdue && !['Completed', 'Dispatched'].includes(t.status));
  const highPri   = tasks.filter(t => ['Critical', 'High'].includes(t.priority) && !['Completed', 'Dispatched'].includes(t.status));
  const dueToday  = tasks.filter(t => { const dl = getDaysLeft(t.due_date); return dl !== null && dl >= 0 && dl <= 1 && !['Completed'].includes(t.status); });
  const completed = tasks.filter(t => ['Completed', 'Dispatched', 'Received'].includes(t.status));
  const activeCount = tasks.length - completed.length;

  if (loading) return <div className="text-zinc-400 animate-pulse p-8">Loading tasks...</div>;

  return (
    <div className="space-y-5" data-testid="taskmanager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <ListChecks size={22} className="text-amber-400" /> Task Manager
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">{activeCount} active · {completed.length} done · {overdue.length} overdue</p>
        </div>
        <div className="flex items-center gap-2">
          {completed.length > 0 && (
            <button onClick={clearCompleted} data-testid="clear-completed-btn"
              className="flex items-center gap-1.5 text-zinc-500 hover:text-red-400 text-xs border border-zinc-800 hover:border-red-800 px-3 py-2 rounded-xl transition-colors">
              <Trash2 size={12} /> Clear done
            </button>
          )}
          <button onClick={() => setShowStandup(true)} data-testid="open-standup-btn"
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs px-3 py-2.5 rounded-xl transition-colors border border-zinc-700"
            title="Log your top 3 tasks for today">
            <Sparkles size={13} /> Daily Standup
          </button>
          <button onClick={() => setShowModal(true)} data-testid="new-task-btn"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm px-4 py-2.5 rounded-xl transition-colors">
            <Plus size={15} /> New Task
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Overdue',      value: overdue.length,    color: 'text-red-400',    bg: 'bg-red-500/10',    icon: AlertTriangle, action: () => { setStatus('active'); setPriority('All'); setModule('All'); } },
          { label: 'Due Today',    value: dueToday.length,   color: 'text-amber-400',  bg: 'bg-amber-500/10',  icon: Clock,         action: () => {} },
          { label: 'High Priority',value: highPri.length,    color: 'text-orange-400', bg: 'bg-orange-500/10', icon: AlertTriangle, action: () => setPriority('High') },
          { label: 'Completed',    value: completed.length,  color: 'text-green-400',  bg: 'bg-green-500/10',  icon: CheckCircle2,  action: () => setStatus('done') },
        ].map(({ label, value, color, bg, icon: Icon, action }) => (
          <button key={label} onClick={action} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center gap-3 hover:border-zinc-700 transition-colors text-left">
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}><Icon size={16} className={color} /></div>
            <div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">{label}</div><div className={`font-heading font-bold text-xl ${color}`}>{value}</div></div>
          </button>
        ))}
      </div>

      {/* Module chips */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(MODULE_CONFIG).map(([mod, cfg]) => {
          const count = tasks.filter(t => t.module === mod && !['Completed', 'Dispatched'].includes(t.status)).length;
          if (count === 0 && moduleFilter !== mod) return null;
          return (
            <button key={mod} onClick={() => setModule(moduleFilter === mod ? 'All' : mod)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-all ${moduleFilter === mod ? 'ring-1 ring-amber-500 ' + cfg.bg + ' ' + cfg.border + ' ' + cfg.color : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              {mod} <span className="font-mono font-bold">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-0.5 gap-0.5">
          {[['active', 'Active'], ['all', 'All'], ['done', 'Done']].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${statusFilter === k ? 'bg-amber-500 text-black font-bold' : 'text-zinc-500 hover:text-zinc-200'}`}>{l}</button>
          ))}
        </div>
        {isPrivileged && (
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-0.5 gap-0.5" data-testid="scope-filter">
            {[['all', 'All Tasks'], ['mine', 'My Tasks']].map(([k, l]) => (
              <button key={k} data-testid={`scope-${k}`} onClick={() => setScope(k)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${scopeFilter === k ? 'bg-blue-500 text-white font-bold' : 'text-zinc-500 hover:text-zinc-200'}`}>{l}</button>
            ))}
          </div>
        )}
        {!isPrivileged && (
          <span className="text-[10px] px-2.5 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-800/40 font-semibold">
            Showing your tasks only
          </span>
        )}
        <select value={priorityFilter} onChange={e => setPriority(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-400 text-xs focus:outline-none">
          {PRIORITIES.map(p => <option key={p} value={p} className="bg-zinc-900">{p}</option>)}
        </select>
        <div className="relative flex-1 min-w-40">
          <Search size={12} className="absolute left-2.5 top-2 text-zinc-600" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-7 pr-3 py-1.5 text-zinc-300 text-xs focus:outline-none focus:border-zinc-600" />
        </div>
        <span className="text-zinc-600 text-[10px] font-mono">{sorted.length} items</span>
      </div>

      {/* ═══ TASK LIST — Checklist style ═══ */}
      <div className="space-y-1" data-testid="task-list">
        {sorted.length === 0 && (
          <div className="text-center py-16 text-zinc-600 bg-zinc-900/50 border border-zinc-800/50 rounded-2xl">
            <CheckCircle2 size={36} className="mx-auto mb-2 text-zinc-700" />
            <p className="text-xs">No tasks match this filter</p>
          </div>
        )}
        {sorted.map(task => {
          const mod = MODULE_CONFIG[task.module] || MODULE_CONFIG.Misc;
          const dl = getDaysLeft(task.due_date);
          const isDone = ['Completed', 'Dispatched', 'Received'].includes(task.status);
          const isOverdue = task.is_overdue && !isDone;
          const isCustom = task.source === 'custom';

          return (
            <div key={task.id} data-testid={`task-${task.id}`}
              className={`group flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
                ${isDone ? 'bg-zinc-900/30 border-zinc-800/40 opacity-50 hover:opacity-70' :
                  isOverdue ? 'bg-red-950/15 border-red-900/40 hover:border-red-700/60' :
                  'bg-zinc-900/60 border-zinc-800/60 hover:border-zinc-700'}`}>

              {/* Checkbox */}
              <button onClick={() => toggleComplete(task)} data-testid={`task-check-${task.id}`}
                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                  ${isDone ? 'border-green-500 bg-green-500/20' : isOverdue ? 'border-red-600 hover:border-red-400' : 'border-zinc-600 hover:border-amber-500'}`}>
                {isDone && <CheckCircle2 size={14} className="text-green-400" />}
              </button>

              {/* Module dot */}
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${mod.dot}`} title={task.module} />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm leading-tight ${isDone ? 'line-through text-zinc-600' : 'text-zinc-100'}`}>
                  {task.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {task.assigned_to && <span className="text-zinc-600 text-[10px]">→ {task.assigned_to}</span>}
                  {task.created_by && task.created_by !== task.assigned_to && <span className="text-zinc-700 text-[10px]">by {task.created_by}</span>}
                  {task.amount > 0 && <span className="text-zinc-600 text-[10px] font-mono">{fmt(task.amount)}</span>}
                </div>
              </div>

              {/* Right side pills */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Priority */}
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.Low}`}>
                  {task.priority?.slice(0, 1)}
                </span>

                {/* Due date */}
                {task.due_date && (
                  <span className={`text-[10px] font-mono ${isOverdue ? 'text-red-400 font-bold' : dl !== null && dl <= 3 ? 'text-amber-400' : 'text-zinc-600'}`}>
                    {isOverdue ? `${Math.abs(dl)}d late` : dl === 0 ? 'Today' : dl === 1 ? 'Tmrw' : task.due_date?.slice(5, 10)}
                  </span>
                )}

                {/* iter205 — Reminder bell badge for tasks with a scheduled
                    fire_at (the linked reminders row gets dismissed when the
                    task is completed, so this never shows for done items). */}
                {task.reminder_at && !isDone && (
                  <span
                    className="text-[10px] font-mono text-amber-400 inline-flex items-center gap-0.5"
                    title={`Reminder: ${new Date(task.reminder_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}
                    data-testid={`reminder-badge-${task.id}`}
                  >
                    <Bell size={10} />
                    {new Date(task.reminder_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}

                {/* Status pill — click to cycle */}
                <button onClick={() => cycleStatus(task)} data-testid={`task-status-${task.id}`}
                  className={`text-[10px] px-2 py-0.5 rounded-full border cursor-pointer transition-all hover:brightness-125 ${STATUS_STYLE[task.status] || STATUS_STYLE.Planning}`}
                  title="Click to advance status">
                  {task.status}
                </button>

                {/* Delete — only custom tasks + admin */}
                {isCustom && isAdmin && (
                  <button onClick={() => requestDelete(`/tasks/${task.id}`, task.title, `Delete "${task.title}" permanently.`)}
                    data-testid={`delete-task-${task.id}`}
                    className="p-1 rounded-lg text-zinc-700 hover:text-red-400 hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Completed section separator */}
      {statusFilter === 'all' && completed.length > 0 && (
        <div className="flex items-center gap-2 pt-2">
          <div className="h-px flex-1 bg-zinc-800" />
          <span className="text-zinc-600 text-[10px] uppercase tracking-widest">
            {completed.length} completed
          </span>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>
      )}

      {/* ═══ CREATE MODAL ═══ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-bold text-zinc-50">New Task</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200"/></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 flex items-center justify-between">
                  <span>What needs to be done? *</span>
                  <VoiceInput
                    onTranscript={(t) => setForm(f => ({ ...f, title: f.title ? `${f.title} ${t}` : t }))}
                    context="task"
                    size={12}
                    title='Tap and speak the task'
                  />
                </label>
                <input required value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" placeholder="Task title..." data-testid="task-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Module</label>
                  <select value={form.module} onChange={e => setForm(f => ({...f, module: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none">
                    {MODULES.filter(m => m !== 'All').map(m => <option key={m} value={m} className="bg-zinc-900">{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none">
                    {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p} value={p} className="bg-zinc-900">{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Amount</label>
                  <input type="number" step="any" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none" placeholder="0" />
                </div>
              </div>
              {/* iter205 — Optional alarm. Fires push + in-app notification
                  at the exact minute. Server expects IST wall-clock; the
                  helper converts on save. */}
              <div>
                <label className="text-zinc-400 text-xs mb-1 flex items-center gap-1.5">
                  <Bell size={11} className="text-amber-400" /> Reminder (date + time)
                </label>
                <div className="flex gap-2">
                  <input type="datetime-local" value={form.reminder_at}
                    onChange={e => setForm(f => ({...f, reminder_at: e.target.value}))}
                    data-testid="reminder-at-input"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  {form.reminder_at && (
                    <button type="button"
                      onClick={() => setForm(f => ({...f, reminder_at: ''}))}
                      data-testid="reminder-clear-btn"
                      className="px-3 rounded-xl border border-zinc-700 text-zinc-500 hover:text-red-400 text-xs"
                      title="Clear reminder">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="text-zinc-600 text-[10px] mt-1">
                  Fires a push notification + in-app alert at the exact minute. Auto-dismisses when the task is marked done.
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Assign To</label>
                <select value={form.assigned_to} onChange={e => setForm(f => ({...f, assigned_to: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none">
                  <option value="">Unassigned</option>
                  {employees.map(em => <option key={em.id} value={em.name} className="bg-zinc-900">{em.name} — {em.role}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                <textarea rows={2} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none resize-none" placeholder="Optional..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="create-task-submit"
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Create Task'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />

      {showStandup && (
        <StandupModal
          forDate={standupForDate}
          onClose={() => { setShowStandup(false); setStandupForDate(null); navigate('/tasks', { replace: true }); }}
          onSeeded={() => { setShowStandup(false); setStandupForDate(null); setStandupDone(true); fetchData(); navigate('/tasks', { replace: true }); }}
        />
      )}
    </div>
  );
}
